import React, { useState, useCallback } from 'react';
import { Scale, Layers } from 'lucide-react';
import ControlTray from '../control-tray/ControlTray';
import { LiveAPIProvider } from "../../contexts/LiveAPIContext";
import { Altair } from '../altair/Altair';
import '../../App.scss';

interface Note {
  id: number;
  content: string;
  timestamp: string;
}

interface CaseSummary {
  caseType: string;
  ipcSections: string;
  nyayaSections: string;
  legalAngles: string;
}

const ModernPage = () => {
  const [notes, setNotes] = useState<Note[]>([]);

  const [caseSummary, setCaseSummary] = useState<CaseSummary>({
    caseType: "",
    ipcSections: "",
    nyayaSections: "",
    legalAngles: "",
  });

  const addNotes = useCallback((note: string) => {
    setNotes(prevNotes => {
      const newNote = {
        id: prevNotes.length + 1,
        content: note,
        timestamp: new Date().toLocaleTimeString()
      };
      return [...prevNotes, newNote];
    });
  }, []);

  const addSummary = useCallback((summary: CaseSummary) => {
    setCaseSummary(summary);
  }, []);

  const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
  if (typeof API_KEY !== "string") {
    throw new Error("set REACT_APP_GEMINI_API_KEY in .env");
  }
  
  const host = "generativelanguage.googleapis.com";
  const uri = `wss://${host}/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent`;
  
  return (
    <div className="app-container">
      <LiveAPIProvider url={uri} apiKey={API_KEY}>
        <header className="header">
          <div className="logo">
            <Scale className="scales-icon" />
            <span>AI Lawyer</span>
          </div>
          <div className="actions">
            <Layers />
          </div>
        </header>

        <main className="main-content">
          <section className="case-notes">
            <div className="case-notes__header">
              <Scale />
              <span>Case Notes</span>
            </div>
            <div className="case-notes__list">
              {notes.map((note) => (
                <div key={note.id} className="note-item">
                  <div className="note-item__content">{note.content}</div>
                  <div className="note-item__timestamp">{note.timestamp}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="case-summary">
            <div className="case-summary__header">
              <Scale />
              <span>Case Summary</span>
            </div>
            
            <div className="case-summary__section">
              <h3>Case Type</h3>
              <p>{caseSummary.caseType}</p>
            </div>

            <div className="case-summary__section">
              <h3>IPC Sections</h3>
              <ul>
                {caseSummary.ipcSections}
              </ul>
            </div>

            <div className="case-summary__section">
              <h3>Nyaya Sections</h3>
              <ul>
                {caseSummary.nyayaSections}
              </ul>
            </div>

            <div className="case-summary__section">
              <h3>Legal Angles</h3>
              <ul>
                {caseSummary.legalAngles}
              </ul>
            </div>
          </section>
        </main>
        <Altair addNotes={(note: string) => addNotes(note)} createSummary={(summary: CaseSummary) => addSummary(summary)} />

        <div className="voice-indicator">
          <ControlTray supportsVideo={false} />
        </div>
      </LiveAPIProvider>
    </div>
  );
};

export default ModernPage;
